
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { TestCollaboratorWithProfile, CollaborationStatus, CollaborationPermission } from "@/types/collaboration";

export function useTestCollaborators(testId: string) {
  return useQuery({
    queryKey: ['test-collaborators', testId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('test_collaborators')
        .select(`
          *,
          collaborator:profiles!test_collaborators_collaborator_id_fkey(id, full_name, email, organization, avatar_url),
          inviter:profiles!test_collaborators_invited_by_fkey(id, full_name, email, organization, avatar_url),
          test:tests(id, title)
        `)
        .eq('test_id', testId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as unknown as TestCollaboratorWithProfile[];
    },
    enabled: !!testId,
  });
}

export function usePendingInvitations() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['pending-invitations', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      console.log('Fetching pending invitations for user:', user.id);
      
      try {
        // First, get the basic collaboration data
        const { data: collaborations, error: collabError } = await supabase
          .from('test_collaborators')
          .select('*')
          .eq('collaborator_id', user.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (collabError) {
          console.error('Error fetching collaborations:', collabError);
          throw collabError;
        }

        if (!collaborations || collaborations.length === 0) {
          console.log('No pending invitations found');
          return [];
        }

        console.log('Found collaborations:', collaborations);

        // Get all unique user IDs for inviter profiles
        const inviterIds = [...new Set(collaborations.map(c => c.invited_by))];
        const testIds = [...new Set(collaborations.map(c => c.test_id))];

        // Fetch inviter profiles
        const { data: inviterProfiles, error: inviterError } = await supabase
          .from('profiles')
          .select('id, full_name, email, organization, avatar_url')
          .in('id', inviterIds);

        if (inviterError) {
          console.error('Error fetching inviter profiles:', inviterError);
          throw inviterError;
        }

        // Fetch test details
        const { data: tests, error: testError } = await supabase
          .from('tests')
          .select('id, title')
          .in('id', testIds);

        if (testError) {
          console.error('Error fetching tests:', testError);
          throw testError;
        }

        // Fetch collaborator profile (current user)
        const { data: collaboratorProfile, error: collaboratorError } = await supabase
          .from('profiles')
          .select('id, full_name, email, organization, avatar_url')
          .eq('id', user.id)
          .single();

        if (collaboratorError) {
          console.error('Error fetching collaborator profile:', collaboratorError);
          throw collaboratorError;
        }

        // Combine the data
        const enrichedInvitations = collaborations.map(collab => {
          const inviter = inviterProfiles?.find(p => p.id === collab.invited_by);
          const test = tests?.find(t => t.id === collab.test_id);
          
          return {
            ...collab,
            collaborator: collaboratorProfile,
            inviter: inviter || { id: collab.invited_by, full_name: 'Unknown', email: 'unknown@example.com', organization: null, avatar_url: null },
            test: test || { id: collab.test_id, title: 'Unknown Test' }
          };
        });

        console.log('Enriched invitations:', enrichedInvitations);
        return enrichedInvitations as TestCollaboratorWithProfile[];
      } catch (error) {
        console.error('Error in usePendingInvitations:', error);
        throw error;
      }
    },
    enabled: !!user?.id,
  });
}

export function useInviteCollaborator() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      testId, 
      email, 
      permission 
    }: { 
      testId: string; 
      email: string; 
      permission: CollaborationPermission; 
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Use the secure database function to lookup the user
      const { data: userLookup, error: lookupError } = await supabase
        .rpc('lookup_user_for_collaboration', {
          lookup_email: email.toLowerCase(),
          inviter_user_id: user.id
        });

      if (lookupError) {
        console.error('User lookup error:', lookupError);
        throw new Error(lookupError.message);
      }

      if (!userLookup || userLookup.length === 0) {
        throw new Error('User not found with this email address or user is not an employer');
      }

      const targetUser = userLookup[0];

      // Create the collaboration invitation
      const { data, error } = await supabase
        .from('test_collaborators')
        .insert({
          test_id: testId,
          collaborator_id: targetUser.user_id,
          invited_by: user.id,
          permission,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('This user has already been invited to collaborate on this test');
        }
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast({
        title: "Invitation sent",
        description: "The collaboration invitation has been sent successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['test-collaborators'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send invitation",
        description: error.message || "An error occurred while sending the invitation.",
        variant: "destructive",
      });
    },
  });
}

export function useRespondToInvitation() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      invitationId, 
      status 
    }: { 
      invitationId: string; 
      status: 'accepted' | 'rejected'; 
    }) => {
      const updateData: any = { status };
      if (status === 'accepted') {
        updateData.accepted_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('test_collaborators')
        .update(updateData)
        .eq('id', invitationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      console.log('Invitation response successful, invalidating caches...');
      
      toast({
        title: variables.status === 'accepted' ? "Invitation accepted" : "Invitation declined",
        description: variables.status === 'accepted' 
          ? "You now have access to collaborate on this test." 
          : "The invitation has been declined.",
      });
      
      // Invalidate pending invitations cache
      queryClient.invalidateQueries({ queryKey: ['pending-invitations'] });
      
      // Invalidate user tests cache so the collaborated test appears in the management table
      if (user?.id) {
        console.log('Invalidating user-tests cache for user:', user.id);
        queryClient.invalidateQueries({ queryKey: ['user-tests', user.id] });
      }
      
      // Also invalidate the general user-tests query
      queryClient.invalidateQueries({ queryKey: ['user-tests'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to respond to invitation",
        description: error.message || "An error occurred while responding to the invitation.",
        variant: "destructive",
      });
    },
  });
}
